import * as z from 'zod';
import {organizationRefSchema, readOrganizationRefSchema} from '../organizations';
import {companySchema} from '../companies';
import {readAudienceRefSchema} from '../audiences';

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readLeadRefSchema = z.object({
  id: z.uuid(),
  tag: z.string().nullable(),
});
export type ReadLeadRef = z.infer<typeof readLeadRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const leadRefSchema = z.object({
  id: z.uuid(),
});

export const leadImportByIdSchema = z.object({
  id: z.uuid(),
});

export const leadImportByDomainSchema = z.object({
  company: z.object({
    domain: z.string().min(1, 'Domain is required'),
  }),
});

export const leadImportByCompanyIdSchema = z.object({
  company: z.object({
    id: z.string().uuid('Company ID must be a valid UUID'),
  }),
});

export const leadImportSchema = z.union([
  leadImportByIdSchema,
  leadImportByDomainSchema,
  leadImportByCompanyIdSchema,
]);

export type LeadImport = z.infer<typeof leadImportSchema>;


export type LeadRef = z.infer<typeof leadRefSchema>;

// Enrichment status enum
export const enrichmentStatusEnum = z.enum(['pending', 'in_progress', 'completed', 'failed']);
export type EnrichmentStatus = z.infer<typeof enrichmentStatusEnum>;

// Lead tag enum (Hot, Warm, Cold)
export const leadTagEnum = z.enum(['hot', 'warm', 'cold']);
export type LeadTag = z.infer<typeof leadTagEnum>;

// Status enum is duplicated locally to avoid the import cycle leads → pages → campaigns → leads.
export const leadPageStatus = z.enum(['draft', 'published', 'unpublished', 'waiting', 'queued', 'generating', 'ready', 'error', 'excluded', 'excluded_conflict']);
export const readLeadPageSchema = z.object({
  id: z.uuid().nullable(),
  status: leadPageStatus.nullable(),
});
export type ReadLeadPage = z.infer<typeof readLeadPageSchema>;

// 3. Read schema - all properties nullable (API response)
export const readLeadSchema = z.object({
  id: z.uuid().nullable(),
  organization: z.lazy(() => readOrganizationRefSchema).nullable(),
  company: companySchema.nullable(),
  tag: leadTagEnum.nullable(),
  audiences: z.lazy(() => z.array(readAudienceRefSchema)).nullable(),
  pages: z.array(readLeadPageSchema).nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadLead = z.infer<typeof readLeadSchema>;

// 4. Create schema - based on DTO serialization groups
export const createLeadSchema = z.object({
  organization: z.lazy(() => organizationRefSchema),
  company: z.object({
    domain: z.string().min(1, 'Domain is required'),
  }),
  tag: z.string().max(100, 'Tag must not exceed 100 characters').optional(),
});
export type CreateLead = z.infer<typeof createLeadSchema>;

// 5. Update schema - based on DTO serialization groups (usually partial)
export const updateLeadSchema = z.object({
  tag: z.string().max(100, 'Tag must not exceed 100 characters').nullable().optional(),
});
export type UpdateLead = z.infer<typeof updateLeadSchema>;

// Legacy export for backward compatibility
export const leadSchema = readLeadSchema;
export type Lead = ReadLead;

// Import context for leads
export const importLeadContextSchema = z.object({
  organization: z.lazy(() => organizationRefSchema),
});
export type ImportLeadContext = z.infer<typeof importLeadContextSchema>;

// Lead import item (for simple list import)
export const importLeadItemSchema = z.object({
  company: z.object({
    domain: z.string().optional(),
    name: z.string().optional(),
  }).optional(),
  tag: leadTagEnum.optional(),
});
export type ImportLeadItem = z.infer<typeof importLeadItemSchema>;
